import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  Search,
  X,
} from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  approveMcpAuthorizationRequest,
  denyMcpAuthorizationRequest,
  getMcpAuthorizationRequest,
  getMcpErrorMessage,
  type McpAuthorizationRequest,
} from '../services/mcpApi.ts';
import { getLoginRedirectForLocation } from '../utils/mcpAuthorization.ts';
import { useUserPermissions } from '../hooks/useUserPermissions.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const SCOPE_LABELS: Record<string, string> = {
  'metrics:funnel:read': 'Consultar funil comercial',
  'metrics:financial:read': 'Consultar resultado comercial',
  'ads:read': 'Consultar anúncios do CRM e do Gerenciador da Meta',
  'crm:report:read': 'Consultar relatório CRM com dados de clientes',
  'conversations:read': 'Consultar conversas e atendimentos da clínica',
};

export const isEmbeddedAuthorizationContext = (
  selfReference: object | null,
  topReference: object | null,
): boolean => selfReference !== topReference;

const ClinicLogo: React.FC<{ logoUrl?: string | null }> = ({ logoUrl }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);
  return (
    <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-500">
      {logoUrl && !failed ? <img src={logoUrl} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="h-full w-full object-contain p-1" /> : <Building2 className="h-5 w-5" />}
    </span>
  );
};

const McpAuthorize: React.FC = () => {
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { status: authStatus, session } = useAuth();
  const { hasPermission } = useUserPermissions();
  const canAccessMcp = session?.userType === 'master' || hasPermission('mcp');
  const requestId = useMemo(
    () => new URLSearchParams(location.search).get('request_id')?.trim() || '',
    [location.search],
  );

  const [authorizationRequest, setAuthorizationRequest] =
    useState<McpAuthorizationRequest | null>(null);
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([]);
  const [clinicSearch, setClinicSearch] = useState('');
  const canSelectMultiple = session?.userType === 'master';
  const [isLoading, setIsLoading] = useState(true);
  const [decision, setDecision] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState('');
  const isEmbedded = isEmbeddedAuthorizationContext(window.self, window.top);

  const loadRequest = async () => {
    if (authStatus === 'loading') {
      return;
    }

    if (isEmbedded) {
      setError(
        'Por segurança, a autorização não pode ser aberta dentro de outro site. Abra esta página diretamente.',
      );
      setIsLoading(false);
      return;
    }

    if (authStatus === 'authenticated' && session?.mustChangePassword) {
      navigate(`/primeiro-acesso?returnTo=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      return;
    }

    if (authStatus === 'anonymous') {
      navigate(
        getLoginRedirectForLocation(location.pathname, location.search),
        { replace: true },
      );
      return;
    }

    if (!canAccessMcp) {
      setError('Sua equipe não possui acesso ao MCP. Solicite a liberação ao administrador da clínica.');
      setIsLoading(false);
      return;
    }

    if (!requestId) {
      setError('Esta solicitação de conexão é inválida ou está incompleta.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setAuthorizationRequest(null);
    setSelectedClinicIds([]);
    setError('');

    try {
      const pendingRequest = await getMcpAuthorizationRequest(requestId);
      setAuthorizationRequest(pendingRequest);
      setSelectedClinicIds(
        pendingRequest.allowed_clinics.length === 1
          ? [pendingRequest.allowed_clinics[0].id]
          : [],
      );
      setClinicSearch('');
    } catch (requestError) {
      setError(
        getMcpErrorMessage(
          requestError,
          'Não foi possível carregar esta solicitação. Ela pode ter expirado ou já ter sido utilizada.',
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadRequest();
    // A carga deve acompanhar somente a solicitação e a rota atuais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessMcp, authStatus, location.pathname, location.search, navigate, requestId, session?.userType, session?.mustChangePassword]);

  const handleApprove = async () => {
    if (!authorizationRequest || selectedClinicIds.length === 0 || decision) {
      return;
    }

    setDecision('approve');
    setError('');

    try {
      const result = await approveMcpAuthorizationRequest(
        authorizationRequest.request_id,
        selectedClinicIds,
      );
      window.location.assign(result.redirect_url);
    } catch (approveError) {
      setError(
        getMcpErrorMessage(
          approveError,
          'Não foi possível autorizar a conexão. Revise a clínica e tente novamente.',
        ),
      );
      setDecision(null);
    }
  };

  const handleDeny = async () => {
    if (!authorizationRequest || decision) {
      return;
    }

    setDecision('deny');
    setError('');

    try {
      const result = await denyMcpAuthorizationRequest(authorizationRequest.request_id);
      window.location.assign(result.redirect_url);
    } catch (denyError) {
      setError(
        getMcpErrorMessage(
          denyError,
          'Não foi possível cancelar a solicitação. Tente novamente.',
        ),
      );
      setDecision(null);
    }
  };

  const pageClasses = isDark
    ? 'bg-[#08111F] text-[#F8FAFC]'
    : 'bg-[#F8F9FC] text-[#121B29]';
  const cardClasses = isDark
    ? 'border-[#344054] bg-[#0F1D31]'
    : 'border-[#E4E8EF] bg-white';

  if (isLoading) {
    return (
      <div className={`flex min-h-[100dvh] items-center justify-center px-4 ${pageClasses}`}>
        <div className="text-center" role="status">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-sminy-primary" />
          <p className={`mt-4 text-sm ${isDark ? 'text-[#98A2B3]' : 'text-gray-600'}`}>
            Carregando solicitação segura...
          </p>
        </div>
      </div>
    );
  }

  if (!authorizationRequest) {
    return (
      <div
        className={`flex min-h-[100dvh] items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))] ${pageClasses}`}
      >
        <div className={`w-full max-w-lg rounded-2xl border p-6 sm:p-8 ${cardClasses}`}>
          <div
            className={`mx-auto flex h-12 w-12 items-center justify-center rounded-xl ${
              isDark ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600'
            }`}
          >
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-center text-lg font-semibold tracking-tight">
            Não foi possível abrir a conexão
          </h1>
          <p
            role="alert"
            className={`mt-2 text-center text-sm ${
              isDark ? 'text-[#98A2B3]' : 'text-gray-600'
            }`}
          >
            {error}
          </p>
          {!isEmbedded
            && requestId
            && canAccessMcp && (
              <button
                type="button"
                onClick={() => void loadRequest()}
                className="mt-6 w-full rounded-xl bg-sminy-primary px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0057DD] focus:outline-none focus:ring-2 focus:ring-sminy-primary/40"
              >
                Tentar novamente
              </button>
            )}
        </div>
      </div>
    );
  }

  const normalizedSearch = clinicSearch.trim().toLocaleLowerCase('pt-BR');
  const visibleClinics = authorizationRequest.allowed_clinics.filter(clinic =>
    clinic.name.toLocaleLowerCase('pt-BR').includes(normalizedSearch),
  );
  const selectedClinicNames = authorizationRequest.allowed_clinics
    .filter(clinic => selectedClinicIds.includes(clinic.id))
    .map(clinic => clinic.name).join(', ');

  const includesCrmReport = authorizationRequest.requested_scopes.includes('crm:report:read');
  const includesAds = authorizationRequest.requested_scopes.includes('ads:read');
  const includesFinancial = authorizationRequest.requested_scopes.includes('metrics:financial:read');

  return (
    <div
      className={`min-h-[100dvh] w-full pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] ${pageClasses}`}
    >
      <main className="mx-auto w-full max-w-[728px] px-4 py-4 sm:px-6 sm:py-8">
        <div className="mb-6">
          <img src={isDark ? '/logo-sminy-white.png' : '/logo-sminy.png'} alt="Sminy" className="h-8 w-auto" />
          <p className="mt-8 text-xs font-medium text-sminy-primary">CONEXÕES · MCP</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">Autorizar conexão</h1>
          <p className={`mt-3 text-sm ${isDark ? 'text-[#98A2B3]' : 'text-[#667085]'}`}>Revise as clínicas e os dados antes de continuar.</p>
        </div>

        <div className={`overflow-hidden rounded-2xl border ${cardClasses}`}>
          <header className={`border-b p-5 sm:p-8 ${isDark ? 'border-[#344054]' : 'border-[#E4E8EF]'}`}>
            <p className={`text-xs font-medium ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>APLICATIVO SOLICITANTE</p>
            <p className={`mt-3 break-words text-sm leading-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong className={isDark ? 'text-white' : 'text-gray-900'}>{authorizationRequest.client.client_name}</strong>{' '}
              solicita acesso aos dados das clínicas que você autorizar.
            </p>
            <p className={`mt-3 text-xs leading-5 ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>
              O nome do aplicativo é informado por ele próprio. Após autorizar, você voltará para{' '}
              <span className="break-all font-medium">{authorizationRequest.client.redirect_host}</span>.
            </p>
          </header>
          <section aria-labelledby="clinic-title" className={`border-b p-5 sm:p-8 ${isDark ? 'border-[#344054]' : 'border-[#E4E8EF]'}`}>
            <h2 id="clinic-title" className="text-sm font-semibold">{canSelectMultiple ? 'Selecione as clínicas' : 'Clínica da conexão'}</h2>
            <p className={`mt-1 text-sm leading-6 ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>
              {canSelectMultiple ? 'Você pode conectar uma ou mais clínicas. O assistente terá acesso somente às selecionadas.' : 'Esta conexão terá acesso somente à sua clínica.'}
            </p>
            {authorizationRequest.allowed_clinics.length > 1 && (
              <div className="relative mt-4">
                <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-[#98A2B3]" />
                <input type="search" aria-label="Buscar clínica" placeholder="Buscar clínica pelo nome" value={clinicSearch}
                  onChange={event => setClinicSearch(event.target.value)} disabled={decision !== null}
                  className={`h-11 w-full rounded-xl border pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-sminy-primary sm:h-10 ${cardClasses}`} />
              </div>
            )}
            {authorizationRequest.allowed_clinics.length === 0 ? (
              <p role="alert" className="mt-4 text-sm">Nenhuma clínica está disponível para esta conexão.</p>
            ) : (
              <div className="mt-4 grid max-h-80 grid-cols-1 gap-3 overflow-y-auto p-1">
                {visibleClinics.map(clinic => {
                  const isSelected = selectedClinicIds.includes(clinic.id);
                  return (
                    <label key={clinic.id} className={`flex min-h-[72px] items-center gap-3 rounded-xl border p-3 transition-colors ${decision !== null ? 'cursor-wait opacity-70' : 'cursor-pointer'} ${isSelected ? isDark ? 'border-sminy-primary bg-[#143A6B]' : 'border-sminy-primary bg-[#EAF2FF]' : cardClasses}`}>
                      <ClinicLogo logoUrl={clinic.logo_url} />
                      <span className="min-w-0 flex-1 break-words text-sm font-medium">
                        {clinic.name}
                        {isSelected && <span aria-hidden="true" className={`mt-1 block text-xs font-normal ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>Selecionada</span>}
                      </span>
                      <input type={canSelectMultiple ? 'checkbox' : 'radio'} name="clinic" value={clinic.id} checked={isSelected} disabled={decision !== null}
                        aria-label={clinic.name}
                        onChange={() => setSelectedClinicIds(current => canSelectMultiple ? current.includes(clinic.id) ? current.filter(id => id !== clinic.id) : [...current, clinic.id] : [clinic.id])}
                        className="h-4 w-4 shrink-0 accent-sminy-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sminy-primary" />
                    </label>
                  );
                })}
                {visibleClinics.length === 0 && (
                  <p role="status" className="py-3 text-sm">Nenhuma clínica encontrada. Tente outro nome.</p>
                )}
              </div>
            )}
            <p role="status" className={`mt-3 text-xs ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>
              {selectedClinicIds.length === 1 ? '1 clínica selecionada' : `${selectedClinicIds.length} clínicas selecionadas`}
            </p>
          </section>
          <section className={`border-b p-5 sm:p-8 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-sminy-primary/10 p-2 text-sminy-primary">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-sm font-semibold">Permissões solicitadas</h2>
                <p className={`mt-1 text-xs ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>
                  {includesCrmReport
                    ? 'A conexão poderá consultar os dados de clientes do CRM das clínicas selecionadas.'
                    : includesAds
                      ? 'A conexão poderá consultar anúncios e resultados agregados das clínicas selecionadas.'
                      : 'A conexão poderá apenas consultar totais agregados.'}
                </p>
              </div>
            </div>

            <ul className="mt-4 space-y-3">
              {authorizationRequest.requested_scopes.map(scope => (
                <li key={scope} className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-sminy-primary" />
                  <span>
                    {scope === 'metrics:financial:read' && includesAds
                      ? 'Consultar valores comerciais, investimento e valores monetários da Meta'
                      : scope === 'metrics:financial:read' && includesCrmReport
                      ? 'Consultar resultado comercial e valores do CRM'
                      : SCOPE_LABELS[scope] || 'Permissão de consulta somente leitura'}
                  </span>
                </li>
              ))}
            </ul>

            <div
              className={`mt-5 rounded-xl border p-4 ${
                isDark
                  ? 'border-[#344054] bg-[#08111F]'
                  : 'border-[#E4E8EF] bg-[#F8F9FC]'
              }`}
            >
              <div className="flex items-start gap-3">
                <LockKeyhole className="mt-0.5 h-5 w-5 flex-shrink-0 text-sminy-primary" />
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {includesCrmReport ? 'Dados compartilhados com este assistente' : 'Sem dados pessoais e sem alterações'}
                  </p>
                  <p className={`mt-1 text-xs leading-relaxed ${isDark ? 'text-[#98A2B3]' : 'text-gray-600'}`}>
                    {includesCrmReport
                      ? 'O relatório inclui nomes, telefones, mídia de origem, tags, datas e status do CRM, incluindo a entrada do lead e o histórico de agendamentos, comparecimentos e compras. Nenhuma mensagem ou prontuário será compartilhado.'
                      : 'Nenhum nome, telefone, mensagem ou prontuário será compartilhado.'}
                    {' '}A conexão não poderá criar ou alterar leads, agendas ou vendas.
                  </p>
                  {includesAds && (
                    <p className={`mt-2 text-xs leading-relaxed ${isDark ? 'text-[#98A2B3]' : 'text-gray-600'}`}>
                      Inclui IDs e nomes de contas, campanhas e anúncios e métricas disponíveis na Meta conectada a cada clínica. Resultados da Meta e do CRM são apresentados separadamente. A conexão não poderá criar ou alterar anúncios.
                    </p>
                  )}
                  {(includesCrmReport || includesAds) && (
                    <p className={`mt-2 text-xs leading-relaxed ${isDark ? 'text-[#98A2B3]' : 'text-gray-600'}`}>
                      {includesAds
                        ? includesFinancial
                          ? 'Esta autorização permite consultar valores comerciais, investimento e valores monetários disponíveis na Meta.'
                          : 'Investimento, valores monetários da Meta e valores comerciais do CRM não serão compartilhados nesta autorização.'
                        : includesFinancial
                        ? 'Esta autorização também permite consultar os valores comerciais do relatório.'
                        : 'Os valores comerciais do relatório não serão compartilhados nesta autorização.'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="p-5 sm:p-8">
            {error && (
              <div
                role="alert"
                className={`mt-4 flex items-start gap-3 rounded-xl border p-4 text-sm ${
                  isDark
                    ? 'border-red-500/20 bg-red-500/10 text-red-300'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Autorize somente se você iniciou esta conexão.</p>
            {selectedClinicNames && (
              <p className={`mt-2 break-words text-xs leading-5 ${isDark ? 'text-[#98A2B3]' : 'text-gray-500'}`}>
                Acesso selecionado: {selectedClinicNames}.
              </p>
            )}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => void handleDeny()}
                disabled={decision !== null}
                className={`inline-flex min-h-11 sm:min-h-10 items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {decision === 'deny' ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                Não autorizar
              </button>
              <button
                type="button"
                onClick={() => void handleApprove()}
                disabled={selectedClinicIds.length === 0 || decision !== null}
                className="inline-flex min-h-11 sm:min-h-10 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0057DD] focus:outline-none focus:ring-2 focus:ring-sminy-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {decision === 'approve' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {decision === 'approve' ? 'Autorizando...' : 'Autorizar conexão'}
              </button>
            </div>
          </section>
        </div>

        <p className={`mt-4 text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          Você poderá revogar esta conexão a qualquer momento nas configurações.
        </p>
      </main>
    </div>
  );
};

export default McpAuthorize;
